// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportDesignerQuerySourceDetail.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import setValue from 'share/utils/Izenda.setValue';
import { updateArray } from 'share/utils/Izenda.ArrayUtils';
import tracker from 'share/tracking/Izenda.Tracker';
import { NODE_TYPE } from 'logic/data/Izenda.CopyManagementConstants';
import { observable, computed, action } from 'mobx';
import { find, filter, flow, map } from 'lodash/fp';
import { DATA_TYPES } from 'logic/data/Izenda.ReportDesignerConstants';
@tracker
class ReportDesignerQuerySourceDetail {
  @observable id;
  @observable name;
  @observable originalName;
  @observable type;
  @observable selected;
  @observable visible;
  @observable querySourceCategoryName;
  @observable connectionName;
  @observable isAlias;
  @observable fields;
  @observable functionName;
  @observable expression;
  @observable izendaDataType;
  @observable dataSourceCategoryId;
  @observable dataSourceCategoryName;
  @observable numOfChilds;
  @observable isDynamic;
  @observable allowToSave;

  @computed
  get aliasId() {
    return `${this.id}_${this.name}`;
  }

  @computed
  get isStoreProcedure() {
    return this.type === NODE_TYPE.STORE_PROCEDURE;
  }

  @computed
  get flattenQuerySource() {
    const querySourceDetail = this;
    const category = this.name;

    return flow(
      filter(
        item =>
          item.izendaDataType !== DATA_TYPES.LOB &&
          item.izendaDataType !== DATA_TYPES.IMAGE &&
          item.izendaDataType !== DATA_TYPES.BOOLEAN
      ),
      map(item => {
        return {
          field: item,
          querySourceDetail: querySourceDetail,
          name: item.name,
          id: item.id,
          category: category
        };
      })
    )(this.fields);
  }

  constructor(detail: any) {
    this.expand = false;
    this.populateData(detail || {});
    this.resetDirty();
  }

  /**
   * Populate data
   * @param {any} detail
   */
  populateData(detail: any) {
    this.id = detail.id || null;
    this.name = detail.name || '';
    this.originalName = detail.originalName || '';
    this.type = detail.type || '';
    this.selected = detail.selected || false;
    this.visible = detail.visible || false;
    this.querySourceCategoryName = detail.querySourceCategoryName || '';
    this.connectionName = detail.connectionName || '';
    this.isAlias = detail.isAlias || false;
    this.fields = detail.fields || [];
    this.functionName = detail.functionName || '';
    this.expression = detail.expression || '';
    this.izendaDataType = detail.izendaDataType || '';
    this.dataSourceCategoryId = detail.dataSourceCategoryId || null;
    this.dataSourceCategoryName = detail.dataSourceCategoryName || '';
    this.numOfChilds = detail.numOfChilds || 0;
    this.isDynamic = detail.isDynamic || false;
    this.expand = detail.expand || false;
    this.allowToSave = detail.allowToSave || false;
  }

  /**
   * Update data
   * @param {any} detail
   */
  @action
  updateData(detail: any) {
    const field = find({ id: detail.id }, this.fields);

    if (field) {
      setValue(field, 'name', detail.name);
      setValue(field, 'expression', detail.expression);
      setValue(field, 'izendaDataType', detail.izendaDataType);

      this.markDirty();

      return this;
    }
    return null;
  }

  /**
   * Check existing field name
   */
  checkExistingFieldName(id: string, newValue: string) {
    const elements = filter(element => {
      return element.name === newValue && element.id !== id;
    }, this.fields);

    return elements && elements.length > 0;
  }
}

export default ReportDesignerQuerySourceDetail;



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ReportDesignerQuerySourceDetail.js